import { useCallback } from 'react';
import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import { SMSDataV4 } from '@frontend/api-sms-data';
import { InboxLocationPhones } from '@frontend/inbox-location-phones';
import { useAppScopeStore } from '@frontend/scope';
import { useMessagingAIRCustomizationFlag } from '@frontend/shared';
import { GetWebsocketEventHandler, useWebsocketEventSubscription } from '@frontend/websocket';

export const useThreadAiStateWSHandler = () => {
  const unifiedThreadUpdater = SMSDataV4._QueryUpdaters.useUnifiedThreadUpdater();
  const { selectedLocationIds } = useAppScopeStore();
  const { isActive: fusionEnabled } = useMessagingAIRCustomizationFlag(selectedLocationIds);
  const checkUserLocationPhoneAccess = InboxLocationPhones.Hooks.useCheckUserLocationPhoneAccess(selectedLocationIds);

  const wsEventHandler = useCallback<GetWebsocketEventHandler<NotificationType.THREAD_AI_STATE>>(
    async (event) => {
      const { params } = event;
      const locationId = params.threadAiActivity.locationId ?? '';
      const threadAiActivity = params.threadAiActivity;

      const weaveNumber = threadAiActivity.locationPhoneNumber;

      // If User does not have access to location phone number in the event's thread, ignore the event
      if (!!weaveNumber && !checkUserLocationPhoneAccess(weaveNumber)) {
        return;
      }

      if (fusionEnabled) {
        await unifiedThreadUpdater.upsertUnifiedThread({
          matchValues: {
            threadId: threadAiActivity.threadId ?? '',
            locationId,
          },
          updater: (thread) => {
            return thread
              ? {
                  ...thread,
                  aiStatus: {
                    ...thread.aiStatus,
                    status: threadAiActivity.status,
                    statusDetails: threadAiActivity.details,
                    isActive: threadAiActivity.isActive,
                    isEscalated: threadAiActivity.isEscalated,
                    isSeeded: threadAiActivity.isSeeded,
                    isBotDeactivatedByPhoneConfig: threadAiActivity.isBotDeactivatedByPhoneConfig,
                  },
                }
              : undefined;
          },
          isOptimistic: false,
        });

        await unifiedThreadUpdater.upsertThreadItem({
          matchValues: {
            threadId: threadAiActivity.threadId ?? '',
            locationId,
            threadItemId: '',
          },
          updater: () => {
            return {
              aiActivity: {
                ...threadAiActivity,
              },
            };
          },
          isOptimistic: false,
        });
      }
    },
    [
      checkUserLocationPhoneAccess,
      fusionEnabled,
      unifiedThreadUpdater.upsertUnifiedThread,
      unifiedThreadUpdater.upsertThreadItem,
    ]
  );

  return wsEventHandler;
};

export const useThreadAiStateWSSubscription = () => {
  const handler = useThreadAiStateWSHandler();
  return useWebsocketEventSubscription(NotificationType.THREAD_AI_STATE, handler);
};
